import { resolveConsentUiLocale, type ConsentUiLocale } from './consent-i18n';

/**
 * The language a consent request is made for, in the order the site-config route honours it:
 * the page's declared language, then the browser's, then English.
 */
export function requestedConsentLocale(): string {
  return document.documentElement.lang || navigator.language || 'en';
}

/** Candidates for the SDK-owned labels, in the same order as `requestedConsentLocale`. */
export function consentLocaleCandidates(): string[] {
  const browser = Array.isArray(navigator.languages) ? navigator.languages : [];
  return [document.documentElement.lang, ...browser, navigator.language].filter(
    (value): value is string => typeof value === 'string' && value.trim() !== ''
  );
}

export interface ConsentLocaleWatchOptions<Policy> {
  /** The UI locale the current copy was fetched for. */
  initialLocale: ConsentUiLocale;
  /** Current language candidates, most preferred first. */
  readCandidates: () => string[];
  /** Fetch the same site config for a language. Null means there is nothing usable to show. */
  fetchPolicy: (signal: AbortSignal) => Promise<Policy | null>;
  /** Apply the copy. Only ever called with the response to the latest request. */
  apply: (policy: Policy, candidates: string[]) => void;
  /** Subscribe to language changes; returns the unsubscribe. */
  subscribe: (onChange: () => void) => () => void;
}

/**
 * Re-fetches localized consent copy when the page changes language without a reload. Requests
 * are keyed by the resolved UI locale, so `de-DE` → `de-AT` does nothing; a newer change aborts
 * the older request, and a response that is no longer the latest is dropped. A failure keeps the
 * last valid copy. Returns a disposer that aborts any request in flight and unsubscribes.
 */
export function watchConsentLocale<Policy>(options: ConsentLocaleWatchOptions<Policy>): () => void {
  let shown: ConsentUiLocale = options.initialLocale;
  let pending: { locale: ConsentUiLocale; controller: AbortController } | null = null;
  let disposed = false;

  const cancelPending = () => {
    pending?.controller.abort();
    pending = null;
  };

  const onChange = () => {
    if (disposed) return;
    const candidates = options.readCandidates();
    const locale = resolveConsentUiLocale(candidates);
    if (pending?.locale === locale) return;
    cancelPending();
    if (locale === shown) return;

    const request = { locale, controller: new AbortController() };
    pending = request;
    options
      .fetchPolicy(request.controller.signal)
      .then((policy) => {
        if (disposed || pending !== request) return;
        pending = null;
        if (!policy) return;
        shown = locale;
        options.apply(policy, candidates);
      })
      .catch(() => {
        // Keep the copy that is already shown; a later change can try again.
        if (pending === request) pending = null;
      });
  };

  const unsubscribe = options.subscribe(onChange);
  // The language may have changed while the first policy was loading.
  onChange();
  return () => {
    disposed = true;
    cancelPending();
    unsubscribe();
  };
}

/** Watches only `<html lang>` and the browser's language list, never the rest of the DOM. */
export function subscribeToPageLanguage(onChange: () => void): () => void {
  const observer =
    typeof MutationObserver === 'function'
      ? new MutationObserver(() => onChange())
      : null;
  observer?.observe(document.documentElement, { attributes: true, attributeFilter: ['lang'] });
  window.addEventListener('languagechange', onChange);
  return () => {
    observer?.disconnect();
    window.removeEventListener('languagechange', onChange);
  };
}
